import { inject, Injectable, signal } from '@angular/core';
import {
  OperatorTypeEnumRequestDto,
  OrderByDirectionEnum,
  OrderByPropertyOfOrderItemPropertyOrderByEnumRequestDto,
  OrderItemFilterRequestDto,
  OrderItemPropertyFilterByEnumRequestDto,
  OrderItemPropertyOrderByEnumRequestDto,
  OrderItemRowTypeEnumRequestDto,
  OrderItemTypeEnumResponseDto,
  OrdersCustomerIdentificationEnumResponseDto,
  PropertyFilterOfOrderItemPropertyFilterByEnumRequestDtoAndOperatorTypeEnumRequestDto
} from '@core/services/apiclient';
import { TranslateService } from '@ngx-translate/core';
import { CustomerService } from '@shared/customer/services/customer.service';
import { MaskInputPatternEnum } from '@shared/models/patterns.enum';
import { TableFilter, TableFilterResult, TableFilterTypeEnum } from '@shared/models/table-filter.model';
import { LoadOptions } from 'devextreme/data';
import { of } from 'rxjs';
import { OrdersCustomerFilterComponent, OrdersEventFilterComponent } from '../components';
import { OrderItemsResultInfo, TreeListColumn } from '../models/dx-tree-list.model';
import { OrderStatusGroupEnum, TreeListRowData } from '../models/orders.model';
import { OrdersService } from './orders.service';

@Injectable({
  providedIn: 'root',
})
export class OrdersTreeListUtilsService {
  private readonly translate = inject(TranslateService);
  private readonly ordersService = inject(OrdersService);
  private readonly customerService = inject(CustomerService);

  readonly resultInfo = signal<{
    ordersCount: number;
    seatCount: number;
    totalAmount: number;
    totalCount: number;
  } | null>(null);

  readonly itemRowTypes = signal<OrderItemRowTypeEnumRequestDto[]>([
    OrderItemRowTypeEnumRequestDto.EventSession,
    OrderItemRowTypeEnumRequestDto.Product,
  ]);

  setResultInfo(resultInfo: OrderItemsResultInfo | null): void {
    this.resultInfo.set(resultInfo);
  }

  setItemRowTypes(itemRowTypes: OrderItemRowTypeEnumRequestDto[]): void {
    this.itemRowTypes.set(itemRowTypes);
  }

  evaluateInternalFilters(types: any[], options: LoadOptions<TreeListRowData>): OrderItemFilterRequestDto {
    if (Array.isArray(options?.filter)) {
      const selectionIds = (options?.filter || [])
        .filter((element) => Array.isArray(element))
        .map((filterArray) => filterArray?.[2]);

      return {
        pagination: {
          current: 0,
          size: 0,
          total: 0,
          enabled: false,
        },
        includes: types,
        criterias: this.buildCriterias([
          {
            result: `[${selectionIds.join(',')}]`,
            field: OrderItemPropertyFilterByEnumRequestDto.OrderNumber,
            type: null,
            condition: OperatorTypeEnumRequestDto.In,
          },
        ]),
        orderBy: {
          direction: OrderByDirectionEnum.Asc,
          property: OrderItemPropertyOrderByEnumRequestDto.OrderNumber,
        },
      };
    }
  }

  buildCriterias(
    filters: TableFilterResult[],
  ): PropertyFilterOfOrderItemPropertyFilterByEnumRequestDtoAndOperatorTypeEnumRequestDto[] {
    return filters.map((filter: TableFilterResult) => ({
      condition: filter?.condition,
      property: filter?.field,
      value: filter?.result,
    }));
  }

  public getOrderPropertyBySelector(sortDescriptor: {
    desc: boolean;
    selector: string;
  }): OrderByPropertyOfOrderItemPropertyOrderByEnumRequestDto {
    return {
      direction: sortDescriptor?.desc ? OrderByDirectionEnum.Desc : OrderByDirectionEnum.Asc,
      property: sortDescriptor?.selector || OrderItemPropertyOrderByEnumRequestDto.OrderNumber,
    } as OrderByPropertyOfOrderItemPropertyOrderByEnumRequestDto;
  }

  getTreeListColumns(): TreeListColumn[] {
    return [
      {
        id: OrderItemPropertyOrderByEnumRequestDto.OrderNumber,
        caption: 'orders.list.orderNumber',
        alignment: 'right',
        width: 170,
        sortIndex: 0,
        sortOrder: 'desc',
        cellTemplate: 'orderNumberColumn',
      },
      {
        id: OrderItemPropertyOrderByEnumRequestDto.BookingDate,
        caption: 'orders.list.bookingDate',
        cellTemplate: 'bookingDateColumn',
      },
      {
        id: OrderItemPropertyOrderByEnumRequestDto.CustomerIdentifier,
        caption: 'orders.list.customerNumber',
        cellTemplate: 'customerIdColumn',
      },
      {
        id: OrderItemPropertyOrderByEnumRequestDto.CustomerName,
        caption: 'orders.list.customerName',
        calculateCellValue: (row: any) => {
          if (row?.customer?.identificationType !== OrdersCustomerIdentificationEnumResponseDto.Anonymous) {
            return [row?.customer?.firstName, row?.customer?.lastName].filter(Boolean).join(' ');
          }
          return null;
        },
        cellTemplate: 'customerNameColumn',
      },
      {
        id: OrderItemPropertyOrderByEnumRequestDto.Label,
        caption: 'orders.list.label',
        calculateCellValue: (row: any) => {
          if (!row) return '';
          const { type, label = '', venueName = '' } = row;
          return type === OrderItemTypeEnumResponseDto.Product
            ? label
            : `${label}${venueName ? ` ${venueName}` : ''}`;
        },
        width: 340,
        cellTemplate: 'orderLabelColumn',
      },
      {
        id: 'category',
        caption: 'orders.list.category',
        allowSorting: false,
        cellTemplate: 'categoriesColumn',
      },
      {
        id: 'priceNames',
        caption: 'orders.list.priceNames',
        allowSorting: false,
        cellTemplate: 'priceNamesColumn',
      },
      {
        id: OrderItemPropertyOrderByEnumRequestDto.OrderItemQuantity,
        caption: 'orders.list.quantity',
        alignment: 'right',
        width: 70,
        cellTemplate: 'quantityColumn',
      },
      {
        id: OrderItemPropertyOrderByEnumRequestDto.OrderAmount,
        caption: 'orders.list.amount',
        alignment: 'right',
        width: 70,
        cellTemplate: 'orderAmountColumn',
      },
      {
        id: OrderItemPropertyOrderByEnumRequestDto.OrderTotalAmount,
        caption: 'orders.list.totalAmount',
        alignment: 'right',
        cellTemplate: 'orderTotalAmountColumn',
        cssClass: 'total-amount-cell',
      },
      {
        id: OrderItemPropertyOrderByEnumRequestDto.SalesUserName,
        caption: 'orders.list.user',
        cellTemplate: 'operatorColumn',
      },
      {
        id: OrderItemPropertyOrderByEnumRequestDto.OrderStatus,
        caption: 'orders.list.status',
        alignment: 'center',
        cellTemplate: 'statusColumn',
      },
    ];
  }

  getOrdersFilters(): TableFilter[] {
    const filters: TableFilter[] = [
      {
        id: 1,
        columns: 'col-sm-4',
        placeholder: this.translate.instant('orders.numberOfOrder'),
        field: OrderItemPropertyFilterByEnumRequestDto.OrderNumber,
        type: TableFilterTypeEnum.Search,
        operator: OperatorTypeEnumRequestDto.Equal,
        isPrimaryFilter: true,
        pattern: MaskInputPatternEnum.Integer,
      },
      {
        id: 2,
        columns: 'col-sm-4',
        placeholder: this.translate.instant('orders.numberOfTicket'),
        field: OrderItemPropertyFilterByEnumRequestDto.SeatNumberOrBarCode,
        type: TableFilterTypeEnum.Search,
        operator: OperatorTypeEnumRequestDto.Equal,
        isPrimaryFilter: true,
      },
      {
        id: 4,
        columns: 'col-sm-4',
        placeholder: this.translate.instant('orders.filter.customerNameEmail'),
        field: OrderItemPropertyFilterByEnumRequestDto.CustomerIdentifier,
        type: TableFilterTypeEnum.Custom,
        customComponent: OrdersCustomerFilterComponent,
        operator: OperatorTypeEnumRequestDto.Equal,
        isPrimaryFilter: true,
      },
      {
        id: 7,
        columns: 'col-sm-3',
        placeholder: this.translate.instant('orders.status.text'),
        field: OrderItemPropertyFilterByEnumRequestDto.OrderStatus,
        type: TableFilterTypeEnum.AutoComplete,
        datasource$: () => {
          const statusEnumKeys = Object.keys(OrderStatusGroupEnum)
            .filter((key) => isNaN(Number(key)))
            .map((value) => ({ id: value, name: `orders.status.${value}` }));
          return of(statusEnumKeys);
        },
        operator: OperatorTypeEnumRequestDto.In,
        idProperty: 'id',
        labelProperty: 'name',
        isMultiselect: true,
      },
      {
        id: 5,
        columns: 'col-sm-4',
        placeholder: 'orders.user',
        field: OrderItemPropertyFilterByEnumRequestDto.SalesUserIdentifier,
        type: TableFilterTypeEnum.AutoComplete,
        datasource$: () => this.customerService.getAllOperators(),
        operator: OperatorTypeEnumRequestDto.In,
        idProperty: 'loginId',
        labelProperty: 'name',
        secondLabelOption: 'userName',
        isMultiselect: true,
      },
      {
        id: 6,
        columns: 'col-sm-6',
        placeholder: this.translate.instant('orders.reservationDate'),
        field: OrderItemPropertyFilterByEnumRequestDto.BookingDate,
        type: TableFilterTypeEnum.Date,
        operator: OperatorTypeEnumRequestDto.In,
      },
      {
        id: 8,
        columns: 'col-sm-7',
        placeholder: this.translate.instant('shared.event'),
        field: OrderItemPropertyFilterByEnumRequestDto.EventSessionIdentifier,
        type: TableFilterTypeEnum.Custom,
        customComponent: OrdersEventFilterComponent,
        operator: OperatorTypeEnumRequestDto.Equal,
        isPrimaryFilter: false,
      },
      {
        id: 9,
        columns: 'col-sm-6',
        placeholder: this.translate.instant('sales.subscription'),
        field: OrderItemPropertyFilterByEnumRequestDto.SubscriptionIdentifier,
        type: TableFilterTypeEnum.AutoComplete,
        datasource$: () => this.ordersService.getSubscriptionsFilter(),
        operator: OperatorTypeEnumRequestDto.In,
        idProperty: 'id',
        labelProperty: 'name',
        isPrimaryFilter: false,
        isMultiselect: true,
      },
      {
        id: 10,
        columns: 'col-sm-6',
        placeholder: this.translate.instant('orders.filter.rate'),
        field: OrderItemPropertyFilterByEnumRequestDto.RateIdentifier,
        type: TableFilterTypeEnum.AutoComplete,
        datasource$: () => this.ordersService.getRatesFilter(),
        operator: OperatorTypeEnumRequestDto.In,
        idProperty: 'id',
        labelProperty: 'name',
        isPrimaryFilter: false,
        isMultiselect: true,
      },
      {
        id: 11,
        columns: 'col-sm-6',
        placeholder: this.translate.instant('orders.filter.paymentMethod'),
        field: OrderItemPropertyFilterByEnumRequestDto.PaymentMethodIdentifier,
        type: TableFilterTypeEnum.AutoComplete,
        datasource$: () => this.ordersService.getPaymentMethodsFilter(),
        operator: OperatorTypeEnumRequestDto.In,
        idProperty: 'id',
        labelProperty: 'name',
        isPrimaryFilter: false,
        isMultiselect: true,
      },
      {
        id: 12,
        columns: 'col-sm-6',
        placeholder: this.translate.instant('orders.filter.category'),
        field: OrderItemPropertyFilterByEnumRequestDto.CategoryIdentifier,
        type: TableFilterTypeEnum.AutoComplete,
        datasource$: () => this.ordersService.getCategoriesFilter(),
        operator: OperatorTypeEnumRequestDto.In,
        idProperty: 'id',
        labelProperty: 'name',
        isPrimaryFilter: false,
        isMultiselect: true,
      },
      {
        id: 13,
        columns: 'col-sm-6',
        placeholder: this.translate.instant('orders.filter.contingent'),
        field: OrderItemPropertyFilterByEnumRequestDto.ContingentIdentifier,
        type: TableFilterTypeEnum.AutoComplete,
        datasource$: () => this.ordersService.getContingentsFilter(),
        operator: OperatorTypeEnumRequestDto.In,
        idProperty: 'id',
        labelProperty: 'name',
        isPrimaryFilter: false,
        isMultiselect: true,
      },
      {
        id: 14,
        columns: 'col-sm-6',
        placeholder: this.translate.instant('orders.filter.block'),
        field: OrderItemPropertyFilterByEnumRequestDto.BlockIdentifier,
        type: TableFilterTypeEnum.AutoComplete,
        datasource$: () => this.ordersService.getBlocksFilter(),
        operator: OperatorTypeEnumRequestDto.In,
        idProperty: 'id',
        labelProperty: 'name',
        isPrimaryFilter: false,
        isMultiselect: true,
      },
    ];

    return filters;
  }

  isFirstOfGroupByIdx(i: number, visibleRows: any[]): boolean {
    if (i <= 0) return true;
    const curr = visibleRows[i]?.data;
    const prev = visibleRows[i - 1]?.data;
    return !prev || prev.orderId !== curr.orderId;
  }

  sameAsNextByIdx(i: number, visibleRows: any[]): boolean {
    const curr = visibleRows[i]?.data;
    const next = visibleRows[i + 1]?.data;
    return !!next && curr?.orderId === next?.orderId;
  }
}
